<template>
    <div class="w-100">
        <!-- Page title -->
        <div class="row align-items-center mt-1 mb-3">
            <div class="col-9">
                <h2 class="page-title">
                    {{ pageTitle }}
                </h2>
            </div>
            <!-- Progress bar -->
            <div v-if="progressBar == true" class="col-3 ms-auto d-print-none d-flex align-items-center" id="progressBar">
                <span class="small me-3 w-50 text-end">{{ percentageComplete }}% Complete</span>
                <b-progress class="w-50" :value="currentStep" :max="maxStep" animated></b-progress>
            </div>
        </div>

        <!-- Alert box -->
        <div v-if="this.v$.step[this.currentStep - 1].$error" class="alert alert-danger alert-dismissible" role="alert">
            <div class="d-flex">
                <div>
                    <!-- Download SVG icon from http://tabler-icons.io/i/alert-circle -->
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon alert-icon"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="currentColor"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                        <path d="M12 8v4"></path>
                        <path d="M12 16h.01"></path>
                    </svg>
                </div>
                <div>
                    <span v-if="v$.$errors.length > 1"> Sorry, there are ({{ this.v$.step[this.currentStep - 1].$errors.length }}) errors found please correct the errors to proceed.</span>
                    <span v-if="v$.$errors.length <= 1"> Sorry, there's ({{ this.v$.step[this.currentStep - 1].$errors.length }}) error found please correct the error to proceed.</span>
                </div>
            </div>
            <a class="btn-close" data-bs-dismiss="alert" aria-label="close"></a>
        </div>

        <form id="create-neurological-observation" action="neurological-observations/store" class="add-neurological-observation add-record">
            <div class="card" v-if="currentStep == 1" title="Observation">
                <div class="card-header">
                    <h3 class="card-title">Observation</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="row">
                                    <div class="col-4">
                                        <div class="form-label">Date of observation</div>
                                        <input
                                            type="date"
                                            class="form-control"
                                            name="dateOfObservation"
                                            placeholder="16:00"
                                            v-model="step[0].dateOfObservation"
                                            :class="{'is-invalid': v$.step[0].dateOfObservation.$invalid}"
                                        />
                                        <div
                                            class="invalid-feedback"
                                            :class="{
                                                'd-block': v$.step[0].dateOfObservation.$invalid,
                                            }"
                                        >
                                            Invalid feedback
                                        </div>
                                    </div>
                                    <div class="col-4">
                                        <div class="form-label">Time of observation</div>
                                        <input
                                            type="time"
                                            class="form-control"
                                            name="timeOfObservation"
                                            placeholder="16:00"
                                            v-model="step[0].timeOfObservation"
                                            :class="{'is-invalid': v$.step[0].timeOfObservation.$invalid}"
                                        />
                                        <div
                                            class="invalid-feedback"
                                            :class="{
                                                'd-block': v$.step[0].timeOfObservation.$invalid,
                                            }"
                                        >
                                            Invalid feedback
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Eyes Open</div>
                                <label v-for="option in eyesOpenOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" name="eyesOpen" :value="option" v-model="step[0].eyesOpen" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].eyesOpen.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="row">
                                    <div class="col-6">
                                        <div class="form-label">Motor Response</div>
                                        <label v-for="option in motorResponseOptions" class="form-check">
                                            <input class="form-check-input" type="radio" name="motorResponse" :value="option" v-model="step[0].motorResponse" />
                                            <span class="form-check-label small">{{ option }}</span>
                                        </label>
                                        <div class="invalid-feedback" :class="{'d-block': v$.step[0].motorResponse.$invalid}">Invalid feedback</div>
                                    </div>
                                    <div class="col-6">
                                        <div class="form-label">Verbal Response</div>
                                        <label v-for="option in verbalResponseOptions" class="form-check">
                                            <input class="form-check-input" type="radio" name="verbalResponse" :value="option" v-model="step[0].verbalResponse" />
                                            <span class="form-check-label small">{{ option }}</span>
                                        </label>
                                        <div class="invalid-feedback" :class="{'d-block': v$.step[0].verbalResponse.$invalid}">Invalid feedback</div>
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="row">
                                    <div class="col-6">
                                        <div class="form-label">Pupil Size (Left)</div>
                                        <select
                                            class="form-select w-75"
                                            name="pupilLeftSize"
                                            v-model="step[0].pupilLeftSize"
                                            :class="{
                                                'is-invalid': v$.step[0].pupilLeftSize.$invalid,
                                            }"
                                        >
                                            <option v-for="(n, i) in pupilMaxSize" :value="n"> 
                                                {{ n }}
                                            </option>
                                        </select>
                                        <div class="invalid-feedback" :class="{'d-block': v$.step[0].pupilLeftSize.$invalid}">Invalid feedback</div>
                                    </div>
                                    <div class="col-6">
                                        <div class="form-label">Pupil Size (Right)</div>
                                        <select
                                            class="form-select w-75"
                                            name="pupilRightSize"
                                            v-model="step[0].pupilRightSize"
                                            :class="{
                                                'is-invalid': v$.step[0].pupilRightSize.$invalid,
                                            }"
                                        >
                                            <option v-for="(n, i) in pupilMaxSize" :value="n"> 
                                                {{ n }}
                                            </option>
                                        </select>
                                        <div class="invalid-feedback" :class="{'d-block': v$.step[0].pupilRightSize.$invalid}">Invalid feedback</div>
                                    </div>
                                </div>
                            </div>


                            <div class="mb-3">
                                <div class="row">
                                    <div class="col-6">
                                        <div class="form-label">Pupil Reaction (Left)</div>
                                        <label v-for="option in pupilReactionOptions" class="form-check">
                                            <input class="form-check-input" type="radio" name="pupilLeftReaction" :value="option" v-model="step[0].pupilLeftReaction" />
                                            <span class="form-check-label">{{ option }}</span>
                                        </label>
                                        <div class="invalid-feedback" :class="{'d-block': v$.step[0].pupilLeftReaction.$invalid}">Invalid feedback</div>
                                    </div>
                                    <div class="col-6">
                                        <div class="form-label">Pupil Reaction (Right)</div>
                                        <label v-for="option in pupilReactionOptions" class="form-check">
                                            <input class="form-check-input" type="radio" name="pupilRightReaction" :value="option" v-model="step[0].pupilRightReaction" />
                                            <span class="form-check-label">{{ option }}</span>
                                        </label>
                                        <div class="invalid-feedback" :class="{'d-block': v$.step[0].pupilRightReaction.$invalid}">Invalid feedback</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="row">
                                    <div class="col-12">
                                        <div class="form-label">Blood Pressure</div>
                                    </div>
                                    <div class="col-3">
                                        <input
                                            type="text"
                                            class="form-control"
                                            name="blood-pressure"
                                            placeholder="Ex. 120"
                                            v-model="step[0].bloodPressure"
                                            :class="{
                                                'is-invalid': v$.step[0].bloodPressure.$invalid,
                                            }"
                                        />
                                        <div
                                            class="invalid-feedback"
                                            :class="{
                                                'd-block': v$.step[0].bloodPressure.$invalid,
                                            }"
                                        >
                                            Invalid feedback
                                        </div>
                                    </div>
                                    <div class="col-1">
                                        <div class="d-flex align-items-center justify-content-center h-100">/</div>
                                    </div>
                                    <div class="col-3">
                                        <input
                                            type="text"
                                            class="form-control"
                                            name="blood-pressure-over"
                                            placeholder="Ex. 80"
                                            v-model="step[0].bloodPressureOver"
                                            :class="{
                                                'is-invalid': v$.step[0].bloodPressureOver.$invalid,
                                            }"
                                        />
                                        <div
                                            class="invalid-feedback"
                                            :class="{
                                                'd-block': v$.step[0].bloodPressureOver.$invalid,
                                            }"
                                        >
                                            Invalid feedback
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Pulse Rate (bpm)</div>
                                <input
                                    type="text"
                                    class="form-control w-50"
                                    name="pulse-rate"
                                    placeholder="Ex. 200"
                                    v-model="step[0].pulseRate"
                                    :class="{
                                        'is-invalid': v$.step[0].pulseRate.$invalid,
                                    }"
                                />
                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].pulseRate.$invalid}">Invalid feedback</div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Respiratory Rate (bpm)</div>
                                <input
                                    type="text"
                                    class="form-control w-50"
                                    name="respiratory-rate"
                                    placeholder="Ex. 20"
                                    v-model="step[0].respiratoryRate"
                                    :class="{
                                        'is-invalid': v$.step[0].respiratoryRate.$invalid,
                                    }"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[0].respiratoryRate.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Temperature (C)</div>
                                <input
                                    type="text"
                                    class="form-control w-50"
                                    name="temperature"
                                    placeholder="Ex. 36"
                                    v-model="step[0].temperature"
                                    :class="{
                                        'is-invalid': v$.step[0].temperature.$invalid,
                                    }"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[0].temperature.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Oxygen Saturation (%)</div>
                                <input
                                    type="text"
                                    class="form-control w-50"
                                    name="oxygen-saturation"
                                    placeholder="Ex. 100"
                                    v-model="step[0].oxygenSaturation"
                                    :class="{
                                        'is-invalid': v$.step[0].oxygenSaturation.$invalid,
                                    }"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[0].oxygenSaturation.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Pupil Scale (mm)</div>
                                <img class="" src="/img/pupil-scale.jpg" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn invisible">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 2" title="Record Details">
                <div class="card-header">
                    <h3 class="card-title">Record Details</h3>
                </div>
                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3 row">
                                <div class="col-6">
                                    <div class="form-label">Glasgow Coma Score</div>
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="glasgowComaScore"
                                        placeholder=""
                                        v-model="step[1].glasgowComaScore"
                                        :class="{'is-invalid': v$.step[1].glasgowComaScore.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[1].glasgowComaScore.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>

                                    <ul class="mt-3">
                                        <li class="mb-2"><strong>13 or higher</strong><br> <small>correlates with a mild brain injury</small></li> 
                                        <li class="mb-2"><strong>9 to 12</strong><br> <small>is a moderate injury</small></li> 
                                        <li class="mb-2"><strong>8 or less</strong><br> <small>a severe brain injury</small></li>
                                    </ul>
                                </div>
                            </div>

                            <div class="form-check mb-2">
                                <input
                                    type="checkbox"
                                    id="discontinueCheck"
                                    v-model="discontinue"
                                    class="form-check-input"
                                />
                                <label for="discontinueCheck" class="form-check-label">
                                    Discontinue Neuro Observations after this.
                                </label>
                            </div>
                            
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn" id="last-save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn" id="last-back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary" id="submit-btn">Finish</a>
                    </p>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import {alpha, alphaNum, email, integer, required, requiredIf, minLength, maxLength, minValue, maxValue, helpers, decimal} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import Multiselect from "vue-multiselect";

// Custom Validators
const inArray = (array) => (value) => array.includes(value) || !helpers.req(value);
const isDateFormat = (value) => value.match("^$|((?:[0-9]{2})?[0-9]{2})-(1[0-2]|0?[1-9])-(3[01]|[12][0-9]|0?[1-9])") || !helpers.req(value);
const isTimeFormat = (value) => value.match("^$|^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$") || !helpers.req(value) || "";
const isTextWithSpecialCharacters = (value) => value.match("^[a-zA-Z0-9_ !@#$&%^*()`.+,/\\-\r\n\s'\"]*$") || !helpers.req(value);
const isTextWithSpace = (value) => value.match("^[a-zA-Z0-9_ \s'\"]*$") || !helpers.req(value) || value >= 0;

export default {
    name: "neurologicalObservationCreateComponent",
    props: ["progressBar", "pageTitle", "tenantId", "residentId", "userId", "accidentIncidentReportId","neurologicalObservation", "operation"], // vue can read kebab props as camel cased vars
    components: {Multiselect},
    setup() {
        return {v$: useVuelidate()};
    },
    data() {
        // If copying, assign neurologicalObservation object to a variable
        if (this.operation == "store") {
            var neurologicalObservationObject = {};
            var endpointToUse = location.protocol + "//" + window.location.host + "/api/neurological-observation/" + process.env.MIX_API_KEY;
        }
        else if (this.operation == 'update') {      
            var neurologicalObservationObject = JSON.parse(this.neurologicalObservation);            
            var endpointToUse = location.protocol + "//" + window.location.host + "/api/neurological-observation/" + neurologicalObservationObject.id + '/' +  process.env.MIX_API_KEY; 
        }

        // Return empty string when object doesn't exist
        // options - array | string
        function returnValueFromObject(fieldName, parent, defaultValue = "", options) {
            // Editing
            if (!_.isEmpty(neurologicalObservationObject)) {
                if (neurologicalObservationObject[parent][fieldName] == null) {
                    return defaultValue;
                } else {
                    return neurologicalObservationObject[parent][fieldName];
                }
            } else {
                return defaultValue;
            }
        }

        return {
            currentStep: 1,
            maxStep: 2,
            percentageComplete: 0,
            errors: 0,
            yesNoOptions: ["Yes", "No"],
            eyesOpenOptions: ["None", "To Pain", "To Speech", "Spontaneously"],
            verbalResponseOptions: ["None", "Incomprehensible Speech", "Inappropriate Speech", "Confused", "Oriented"],
            motorResponseOptions: ["None", "Extension to Pain", "Flexation to Pain", "Localised to Pain", "Withdraws (pain)"],
            pupilReactionOptions: ["Closed", "No Reaction", "Reaction"],
            pupilMaxSize: 8,
            host: document.location.origin,
            endpoint: endpointToUse,
            step: [
                // Step 1
                {
                    dateOfObservation: returnValueFromObject("dateOfObservation", "observations"),
                    timeOfObservation: returnValueFromObject("timeOfObservation", "observations"),
                    eyesOpen: returnValueFromObject("eyesOpen", "observations"),
                    verbalResponse: returnValueFromObject("verbalResponse", "observations"),
                    motorResponse: returnValueFromObject("motorResponse", "observations"),
                    pupilLeftSize: returnValueFromObject("pupilLeftSize", "observations"),
                    pupilLeftReaction: returnValueFromObject("pupilLeftReaction", "observations"),
                    pupilRightSize: returnValueFromObject("pupilRightSize", "observations"),
                    pupilRightReaction: returnValueFromObject("pupilRightReaction", "observations"),
                    bloodPressure: returnValueFromObject("bloodPressure", "observations"),
                    bloodPressureOver: returnValueFromObject("bloodPressureOver", "observations"),
                    pulseRate: returnValueFromObject("pulseRate", "observations"),
                    respiratoryRate: returnValueFromObject("respiratoryRate", "observations"),
                    temperature: returnValueFromObject("temperature", "observations"),
                    oxygenSaturation: returnValueFromObject("oxygenSaturation", "observations"),
                },
                // Step 2
                {
                    glasgowComaScore: returnValueFromObject("glasgowComaScore", "record_details"),
                },
            ],
            discontinue: false,
            commencedBy: this.userId,
            commencedByDate:  new Date().toISOString().split("T")[0] 
        };
    },
    validations() {
        const dateType = {
            required,
            isDateFormat,
            $lazy: true,
            $autoDirty: true,
        };

        const timeType = {
            required,
            isTimeFormat,
            $lazy: true,
            $autoDirty: true,
        };

        // TEXT TYPES: default: 191, mid:500, long:1000
        function text(minimum = 3, maximum = 191, isRequired = false) {
            var rule = {
                required,
                isTextWithSpecialCharacters,
                minLength: minLength(minimum),
                maxLength: maxLength(maximum),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) {
                rule = Object.assign({}, rule);
                delete rule["required"];
            }
            return rule;
        }

        // INT TYPES:
        function intType(minimum = 0, maximum = 10, isRequired = false) {
            var rule = {
                required,
                integer,
                minValue: minValue(minimum),
                maxValue: maxValue(maximum),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) {
                rule = Object.assign({}, rule);
                delete rule["required"];
            }
            return rule;
        }

        // DECIMAL TYPES:
        function decimalType(minimum = 0, maximum = 10, isRequired = false) {
            var rule = {
                required,
                decimal,
                minValue: minValue(minimum),
                maxValue: maxValue(maximum),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) {
                rule = Object.assign({}, rule);
                delete rule["required"];
            }
            return rule;
        }

        // ARRAY TYPES
        function inOptions(options, isRequired = false) {
            var rule = {
                required,
                inOptions: inArray(options),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) {
                rule = Object.assign({}, rule);
                delete rule["required"];
            }
            return rule;
        }

        // Use for types with no 'required' parameter
        function notRequired(type) {
            var validations = Object.assign({}, type);
            delete validations["required"];

            return validations;
        }

        return {
            step: [
                // Step 1
                {
                    dateOfObservation: notRequired(dateType),
                    timeOfObservation: notRequired(timeType),
                    eyesOpen: inOptions(this.eyesOpenOptions, true),
                    verbalResponse: inOptions(this.verbalResponseOptions, true),
                    motorResponse: inOptions(this.motorResponseOptions, true),
                    pupilLeftSize: intType(1, 8, true),
                    pupilLeftReaction: inOptions(this.pupilReactionOptions, true),
                    pupilRightSize: intType(1, 8, true),
                    pupilRightReaction: inOptions(this.pupilReactionOptions, true),
                    bloodPressure: decimalType(0, 300, true),
                    bloodPressureOver: decimalType(0, 300, true),
                    pulseRate: decimalType(0, 300, true),
                    respiratoryRate: decimalType(0, 300, true),
                    temperature: decimalType(0, 300, true),
                    oxygenSaturation: decimalType(0, 300, true),
                },

                // Step 2
                {
                    glasgowComaScore: text(3, 10),
                },
            ],
        };
    },

    computed: {},

    methods: {
        onClickNext: function () {
            // Validate fields on current step
            this.v$.step[this.currentStep - 1].$touch();

            // if there are no errors in the current step
            if (!this.v$.step[this.currentStep - 1].$error) {
                // All steps
                if (this.currentStep < this.maxStep) {
                    // Proceed to next step
                    this.currentStep++;

                    // Final step
                } else if (this.currentStep == this.maxStep) {
                    console.log("last step");
                    // Initialize alerts and toast notifications for displaying message
                    const toastError = Swal.mixin({
                        toast: true,
                        position: "top-end",
                        showConfirmButton: true,
                    });
                    const alertSuccess = Swal.mixin({
                        position: "center",
                        showConfirmButton: true,
                        timer: 3000,
                        timerProgressBar: true,
                    });                    

                    // Show button spinner
                    document.getElementById("submit-btn").innerHTML = '<i class="fa fa-circle-notch fa-spin"></i>';
                    document.getElementById("submit-btn").disabled = true;
                    document.getElementById("last-back-btn").setAttribute("style", "display:none !important");
                    document.getElementById("last-save-btn").setAttribute("style", "display:none !important");

                    // Loop through all fields
                    const formData = {};

                    for (let i = 0; i < this.maxStep; i++) {
                        for (const [key, value] of Object.entries(this.step[i])) {
                            // formData.append(key, value);
                            formData[key] = value;
                        }
                    }

                    // Add residentID and userID
                    formData["tenantID"] = this.tenantId;
                    formData["residentID"] = this.residentId;
                    formData["accidentIncidentReportID"] = this.accidentIncidentReportId;
                    formData["userID"] = this.userId;

                    if (this.discontinue) {
                        formData['discontinuedBy'] = this.userId;
                        formData['discontinuedByDate']  = new Date().toISOString().split("T")[0];
                    }

                    if (this.operation == "store") {
                        formData['commencedBy'] = this.userId;
                        formData['commencedByDate']  = new Date().toISOString().split("T")[0];
                        
                        this.postNeurologicalObservation(formData, toastError, alertSuccess);
                    }
                    else if (this.operation == "update") {                        
                        this.updateNeurologicalObservation(formData, toastError, alertSuccess);
                    }
                }
                this.percentageComplete = Math.round((this.currentStep / this.maxStep) * 100);
            }
        },

        postNeurologicalObservation: function (formData, toastError, alertSuccess) {            
            // Call the store endpoint
            axios
                .post(this.endpoint, formData)
                .then((response) => {
                    if (response.data.error == 403) {
                        console.log("Bad Request");
                        console.log(response.data);

                        // Show bad request toast message. This error occurs when the input fields are not in the expected format
                        toastError.fire({
                            type: "warning",
                            title: "The input fields are malformed and cant be processed.",
                        });
                    } else {
                        if (response.data.id == null) {
                            console.log("Validation Error");
                            console.log(response.data);

                            // Show validation toast message. This error only occurs when the javascript validation is bypassed and the API endpoint returned validation error instead of the resident object
                            toastError.fire({
                                type: "warning",
                                title: "There are validation errors found on the form.",
                            });
                        } else {
                            console.log("Success");
                            console.log(response.data);

                            // Show success alert message and redirect to the index page. This occurs when the form submission is successful and the resident object was added to the DB. Thi
                            this.formSuccess = true;

                            alertSuccess
                                .fire({
                                    title: "Success!",
                                    text: "The neurological observation has been added.",
                                    icon: "success",
                                })
                                .then(function () {
                                    window.location = "/accident-incident-reports/" + formData["accidentIncidentReportID"];
                                });
                        }
                    }
                })
                .catch((err) => {
                    console.log("Endpoint Error!");
                    console.log(err.response);
                    toastError.fire({type: "warning", title: "Sorry, request failed to submit."}); // this error occurs when endpoint is wrong
                });
        },
        
        updateNeurologicalObservation: function (formData, toastError, alertSuccess) {            
            // Call the store endpoint
            axios
                .put(this.endpoint, formData)
                .then((response) => {
                    if (response.data.error == 403) {
                        console.log("Bad Request");
                        console.log(response.data);

                        // Show bad request toast message. This error occurs when the input fields are not in the expected format
                        toastError.fire({
                            type: "warning",
                            title: "The input fields are malformed and cant be processed.",
                        });
                    } else {
                        if (response.data.id == null) {
                            console.log("Validation Error");
                            console.log(response.data);

                            // Show validation toast message. This error only occurs when the javascript validation is bypassed and the API endpoint returned validation error instead of the resident object
                            toastError.fire({
                                type: "warning",
                                title: "There are validation errors found on the form.",
                            });
                        } else {
                            console.log("Success");
                            console.log(response.data);

                            // Show success alert message and redirect to the index page. This occurs when the form submission is successful and the resident object was added to the DB. Thi
                            this.formSuccess = true;

                            alertSuccess
                                .fire({
                                    title: "Success!",
                                    text: "The neurological observation has been updated.",
                                    icon: "success",
                                })
                                .then(function () {
                                    window.location = "/accident-incident-reports/" + formData["accidentIncidentReportID"];
                                });
                        }
                    }
                })
                .catch((err) => {
                    console.log("Endpoint Error!");
                    console.log(err.response);
                    toastError.fire({type: "warning", title: "Sorry, request failed to submit."}); // this error occurs when endpoint is wrong
                });
        },

        onClickBack: function () {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
            this.percentageComplete = Math.round((this.currentStep / this.maxStep) * 100);
        },
    },
};
</script>
